// Impresos de la cuenta corriente con el formato de Random (pedido de Rodrigo, 2026-10-14): cartola de
// cliente y de proveedor («CARTOLA DE ENTIDAD RESUMIDA / CONTABLE»), comprobante de ingreso (cobro) y
// comprobante de egreso (pago a proveedor). Texto de ancho fijo, como el manifiesto: aquí solo se arma
// el texto; `Reporte` lo dibuja en monoespaciado.
import {
  centro,
  ddmmaaaa,
  der,
  izq,
  miles,
  rutEmpresa,
  type LineaReporte,
} from '../logistica/reporteRandom';

// Vertical con letra grande, igual que el manifiesto.
const ANCHO = 96;
const GUIONES = '-'.repeat(ANCHO);

export interface EmpresaImpresa {
  razonSocial: string;
  rut: string;
  dv: string;
}

/** Los datos de la entidad que van bajo el título (salen de su ficha). */
export interface EntidadImpresa {
  rut: string | null;
  nombre: string;
  direccion?: string | null;
  fono?: string | null;
  lineaCredito?: string | null;
}

/** Un documento con su saldo, para la sección de pendientes y el vencimiento de la cartola. */
export interface DocumentoImpreso {
  documentoId: string;
  td: string;
  folio: string | null;
  emision: string;
  vence: string | null;
  diasVencido: number;
  valor: string;
  saldo: string;
}

export interface MovimientoImpreso {
  fecha: string;
  descripcion: string;
  documentoId: string | null;
  cargo: string;
  abono: string;
  saldo: string;
  /** Columnas separadas (si vienen): tipo con código Random, número, NV, vencimiento y detalle. */
  td?: string;
  numero?: string;
  nv?: string | null;
  vence?: string | null;
  detalle?: string;
}

/** Día local de un instante (el ISO de un timestamp viene en UTC). */
export const diaDe = (ts: string) =>
  ts.length > 10 ? new Date(ts).toLocaleDateString('sv-SE') : ts;
const n = (v: string | number) => Number(v) || 0;
const plata = (v: string | number, ancho: number) => der(miles(n(v)), ancho);
const fila = (...celdas: string[]) => ({ texto: celdas.join(' ').trimEnd() });

function cabecera(empresa: EmpresaImpresa, titulo: string): LineaReporte[] {
  const hoy = ddmmaaaa(new Date().toLocaleDateString('sv-SE'));
  const derecha = (a: string, b: string) => ({ texto: izq(a, ANCHO - b.length) + b });
  return [
    derecha(empresa.razonSocial.toUpperCase(), `Fecha :${hoy}`),
    derecha(`R.U.T.:${rutEmpresa(empresa.rut, empresa.dv)}`, 'Pagina : 000001'),
    { texto: centro(titulo, ANCHO), titulo: true },
  ];
}

function datosEntidad(e: EntidadImpresa, rotulo: string): LineaReporte[] {
  const out: LineaReporte[] = [
    { texto: centro(`${rotulo}: ${e.rut ?? ''}  ${e.nombre.toUpperCase()}`, ANCHO) },
  ];
  const dir = [
    e.direccion && `Direccion: ${e.direccion.toUpperCase()}`,
    e.fono && `Fono: ${e.fono}`,
  ]
    .filter(Boolean)
    .join('   ');
  if (dir) out.push({ texto: centro(dir, ANCHO) });
  if (e.lineaCredito && n(e.lineaCredito) > 0)
    out.push({ texto: centro(`Moneda Cta.Cte: $ / Cr.Total: ${miles(n(e.lineaCredito))}`, ANCHO) });
  return out;
}

/** Un documento que sigue debiendo (del ERP o heredado de Random al corte). */
export interface PendienteImpreso {
  td: string;
  numero: string;
  nv?: string | null;
  emision: string;
  vence: string | null;
  diasVencido: number;
  valor: string;
  saldo: string;
  heredado?: boolean;
  /** Abonos que ya tiene, con su medio (trazabilidad): una línea bajo el documento. */
  pagos?: Array<{ recibo: string; fecha: string; medio: string; monto: string }>;
}

// Horizontal como la de Random, angosta para que la letra salga grande (2026-10-17).
const ANCHO_CARTOLA = 128;
/** «24/08/26», como Random. */
const ddmmaa = (iso: string) => {
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a!.slice(2)}`;
};

/**
 * Cartola de cliente o proveedor con el formato de Random («CARTOLA DE ENTIDAD RESUMIDA / CONTABLE»,
 * pedido de Rodrigo, 2026-10-17: «enfócate en que salga la información de deuda clara»): una línea
 * por documento que debe, con su valor, lo abonado, su saldo y el saldo acumulado de la cuenta; al
 * final lo vencido, lo por vencer y el nuevo saldo.
 */
export function textoCartola(p: {
  empresa: EmpresaImpresa;
  entidad: EntidadImpresa;
  proveedor?: boolean;
  saldoAFavor?: string | undefined;
  pendientes: PendienteImpreso[];
  /** La que se le manda al cliente (PDF): sin la columna de la nota de venta, que es interna. */
  paraCliente?: boolean;
}): LineaReporte[] {
  const conNv = !p.paraCliente;
  const A = conNv ? ANCHO_CARTOLA : ANCHO_CARTOLA - 13;
  const hoyIso = new Date().toLocaleDateString('sv-SE');
  const derecha = (a: string, b: string) => ({ texto: izq(a, A - b.length) + b });
  const e = p.entidad;
  const deuda = p.pendientes.reduce((s, d) => s + n(d.saldo), 0);
  const vencido = p.pendientes.filter((d) => d.diasVencido > 0).reduce((s, d) => s + n(d.saldo), 0);
  const aFavor = n(p.saldoAFavor ?? 0);
  const cupo = n(e.lineaCredito ?? 0);
  const rutSolo = (e.rut ?? '').replace(/\./g, '').split('-')[0] ?? '';

  const credito =
    cupo > 0
      ? `Moneda Cta.Cte: $ / Cr.Total: ${miles(cupo)} / Utilizado: ${miles(deuda)} / Disponible: ${miles(cupo - deuda)}`
      : 'Moneda Cta.Cte: $ / Sin linea de credito';
  const out: LineaReporte[] = [
    derecha(p.empresa.razonSocial.toUpperCase(), `Fecha :${ddmmaaaa(hoyIso)}`),
    derecha(`R.U.T.:${rutEmpresa(p.empresa.rut, p.empresa.dv)}`, 'Pagina : 000001'),
    {
      texto: centro(
        p.proveedor
          ? 'CARTOLA DE PROVEEDOR RESUMIDA / CONTABLE'
          : 'CARTOLA DE ENTIDAD RESUMIDA / CONTABLE',
        A,
      ),
      titulo: true,
    },
    { texto: centro(`Entidad: ${rutSolo}  ${e.nombre.toUpperCase()}`, A) },
    {
      texto: centro(
        [e.direccion && `Direccion: ${e.direccion.toUpperCase()}`, e.fono && `Fono: ${e.fono}`]
          .filter(Boolean)
          .join('   '),
        A,
      ),
    },
    { texto: centro(credito, A) },
    { texto: '' },
    { texto: '_'.repeat(A) },
    fila(
      izq('TD', 4),
      izq('N.Interno', 11),
      ...(conNv ? [izq('Nota Venta', 12)] : []),
      izq('Fec.Emi.', 8),
      izq('Fec.Venc', 8),
      der('Dias', 5),
      der('$ Valor Doc', 14),
      der('$ Saldo Doc', 14),
      der('$ Cargos', 14),
      der('$ Abonos', 14),
      der('$ Saldo Cta', 14),
    ),
    { texto: '_'.repeat(A) },
    { texto: '' },
  ];
  let corrido = 0;
  let cargos = 0;
  let abonos = 0;
  for (const d of p.pendientes) {
    const valor = n(d.valor);
    const saldo = n(d.saldo);
    corrido += saldo;
    cargos += valor;
    abonos += valor - saldo;
    out.push(
      fila(
        izq(d.td, 4),
        izq(d.numero.padStart(10, '0'), 11),
        ...(conNv ? [izq(d.heredado ? '(Random)' : (d.nv ?? ''), 12)] : []),
        izq(ddmmaa(d.emision), 8),
        izq(d.vence ? ddmmaa(d.vence) : '', 8),
        der(d.diasVencido > 0 ? String(d.diasVencido) : '', 5),
        plata(valor, 14),
        plata(saldo, 14),
        plata(valor, 14),
        plata(valor - saldo, 14),
        plata(corrido, 14),
      ),
    );
    // Trazabilidad: con qué se abonó (recibo, fecha y medio).
    const porRecibo = new Map<string, { fecha: string; partes: string[] }>();
    for (const x of d.pagos ?? []) {
      const r = porRecibo.get(x.recibo) ?? { fecha: x.fecha, partes: [] };
      r.partes.push(`${MEDIO_RANDOM[x.medio]?.slice(0, 3) ?? x.medio} ${miles(n(x.monto))}`);
      porRecibo.set(x.recibo, r);
    }
    for (const [recibo, r] of porRecibo)
      out.push({
        texto: `      abono ${ddmmaa(diaDe(r.fecha))} ${recibo}: ${r.partes.join(' + ')}`.slice(
          0,
          A,
        ),
      });
  }
  if (!p.pendientes.length) out.push({ texto: 'Sin documentos con saldo: la cuenta esta al dia.' });
  out.push(
    { texto: '-'.repeat(A) },
    fila(izq('TOTALES $', conNv ? 83 : 70), plata(cargos, 14), plata(abonos, 14), plata(deuda, 14)),
    { texto: '='.repeat(A) },
    { texto: '' },
    fila(izq('TOTAL VENCIDO $', 28), der(dosDecimalesCl(vencido), 16)),
    fila(izq('TOTAL POR VENCER $', 28), der(dosDecimalesCl(deuda - vencido), 16)),
  );
  if (aFavor > 0)
    out.push(fila(izq('ANTICIPOS SIN APLICAR $', 28), der(`-${dosDecimalesCl(aFavor)}`, 16)));
  out.push(fila(izq('NUEVO SALDO $', 28), der(dosDecimalesCl(deuda - aFavor), 16)));
  return out;
}

// ---------------------------------------------------------------- cartola por vendedor

type EstadoImpreso = 'EN_MORA' | 'VENCIDO_TOLERADO' | 'AL_DIA' | 'SIN_DEUDA';
type TramosImpresos = Record<'corriente' | '1-30' | '31-60' | '61-90' | '91+', string>;

/** Un cliente de la cartera, con lo que debe (los datos de `/tesoreria/cxc/por-vendedor`). */
export interface CuentaImpresa {
  cliente: string;
  rut: string | null;
  vendedorId: string | null;
  condicionPago: string;
  plazoDias: number;
  diasMorosidad: number;
  bloqueado: boolean;
  estado: EstadoImpreso;
  saldo: string;
  vencido: string;
  tramos: TramosImpresos;
  saldoAFavor: string;
  diasMaxVencido: number;
  pendientes: PendienteImpreso[];
}

export interface VendedorImpreso {
  vendedorId: string | null;
  codigo: string | null;
  nombre: string;
  cartera: number;
  clientes: number;
  porEstado: Record<EstadoImpreso, number>;
  saldo: string;
  vencido: string;
  tramos: TramosImpresos;
  saldoAFavor: string;
  neto: string;
}

export const ESTADO_CUENTA_IMPRESO: Record<EstadoImpreso, string> = {
  EN_MORA: 'EN MORA',
  VENCIDO_TOLERADO: 'TOLERADO',
  AL_DIA: 'AL DIA',
  SIN_DEUDA: 'SIN DEUDA',
};

/** «CREDITO 30d», «CONTADO». */
const condicionImpresa = (c: Pick<CuentaImpresa, 'condicionPago' | 'plazoDias'>) =>
  `${(c.condicionPago || 'sin condicion').toUpperCase()}${c.plazoDias > 0 ? ` ${c.plazoDias}d` : ''}`;

/**
 * Cartola general de cuentas corrientes por vendedor (como la de Random, Rodrigo 2026-10-29): por cada
 * vendedor, sus clientes (los que su ficha le asigna) con el estado de la cuenta; `detalle` agrega los
 * documentos que deben. Al pie de cada vendedor, sus totales por tramo de vencimiento.
 */
export function textoCartolaVendedor(p: {
  empresa: EmpresaImpresa;
  /** «Vendedor: V01 JONATHAN CORNEJO» o «Todos los vendedores». */
  titulo: string;
  vendedores: VendedorImpreso[];
  clientes: CuentaImpresa[];
  detalle: boolean;
}): LineaReporte[] {
  const A = ANCHO_CARTOLA;
  const hoyIso = new Date().toLocaleDateString('sv-SE');
  const derecha = (a: string, b: string) => ({ texto: izq(a, A - b.length) + b });
  const out: LineaReporte[] = [
    derecha(p.empresa.razonSocial.toUpperCase(), `Fecha :${ddmmaaaa(hoyIso)}`),
    derecha(`R.U.T.:${rutEmpresa(p.empresa.rut, p.empresa.dv)}`, 'Pagina : 000001'),
    { texto: centro('CARTOLA GENERAL DE CUENTAS CORRIENTES POR VENDEDOR', A), titulo: true },
    { texto: centro(p.titulo.toUpperCase(), A) },
    { texto: '' },
    { texto: '_'.repeat(A) },
    p.detalle
      ? fila(
          izq('TD', 4),
          izq('N.Interno', 11),
          izq('Nota Venta', 12),
          izq('Fec.Emi.', 8),
          izq('Fec.Venc', 8),
          der('Dias', 5),
          der('$ Valor Doc', 14),
          der('$ Saldo Doc', 14),
        )
      : fila(
          izq('RUT', 11),
          izq('Cliente', 26),
          izq('Estado', 10),
          der('$ Saldo', 12),
          der('Por vencer', 11),
          der('1-30', 11),
          der('31-60', 11),
          der('61-90', 11),
          der('+90', 11),
          der('Dias', 4),
        ),
    { texto: '_'.repeat(A) },
  ];
  const varios = p.vendedores.length > 1;
  for (const v of p.vendedores) {
    if (varios)
      out.push(
        { texto: '' },
        { texto: `VENDEDOR: ${v.codigo ?? ''} ${v.nombre}`.replace(/\s+/g, ' '), titulo: true },
      );
    for (const c of p.clientes.filter((x) => x.vendedorId === v.vendedorId)) {
      const estado = `${ESTADO_CUENTA_IMPRESO[c.estado]}${c.bloqueado ? ' / BLOQUEADO' : ''}`;
      if (!p.detalle) {
        out.push(
          fila(
            izq(c.rut ?? '', 11),
            izq(c.cliente.toUpperCase(), 26),
            izq(estado, 10),
            plata(c.saldo, 12),
            plata(c.tramos.corriente, 11),
            plata(c.tramos['1-30'], 11),
            plata(c.tramos['31-60'], 11),
            plata(c.tramos['61-90'], 11),
            plata(c.tramos['91+'], 11),
            der(c.diasMaxVencido > 0 ? String(c.diasMaxVencido) : '', 4),
          ),
        );
        continue;
      }
      const atraso = c.diasMaxVencido > 0 ? ` (${c.diasMaxVencido} dias)` : '';
      const tolerancia =
        c.diasMorosidad > 0 ? `   Morosidad permitida: ${c.diasMorosidad} dias` : '';
      out.push(
        { texto: '' },
        {
          texto:
            `Entidad: ${c.rut ?? ''}  ${c.cliente.toUpperCase()}   Cond: ${condicionImpresa(c)}   Estado: ${estado}${atraso}${tolerancia}`.slice(
              0,
              A,
            ),
        },
      );
      for (const d of c.pendientes)
        out.push(
          fila(
            izq(d.td, 4),
            izq(d.numero.padStart(10, '0'), 11),
            izq(d.heredado ? '(Random)' : (d.nv ?? ''), 12),
            izq(ddmmaa(d.emision), 8),
            izq(d.vence ? ddmmaa(d.vence) : '', 8),
            der(d.diasVencido > 0 ? String(d.diasVencido) : '', 5),
            plata(d.valor, 14),
            plata(d.saldo, 14),
          ),
        );
      const aFavor = n(c.saldoAFavor) > 0 ? `   A favor $ ${miles(n(c.saldoAFavor))}` : '';
      out.push({
        texto: `${' '.repeat(10)}Total cliente $ ${miles(n(c.saldo))}   Vencido $ ${miles(n(c.vencido))}${aFavor}`,
      });
    }
    out.push(...totalesImpresos(`TOTAL ${v.codigo ?? ''} ${v.nombre}`.replace(/\s+/g, ' '), v, A));
  }
  if (varios) {
    const suma = (f: (v: VendedorImpreso) => string | number) =>
      p.vendedores.reduce((s, v) => s + n(f(v)), 0);
    const tramo = (k: keyof TramosImpresos) => String(suma((v) => v.tramos[k]));
    const estados = (k: EstadoImpreso) => suma((v) => v.porEstado[k]);
    out.push(
      { texto: '' },
      { texto: '='.repeat(A) },
      ...totalesImpresos(
        'TOTAL GENERAL',
        {
          cartera: suma((v) => v.cartera),
          clientes: suma((v) => v.clientes),
          porEstado: {
            EN_MORA: estados('EN_MORA'),
            VENCIDO_TOLERADO: estados('VENCIDO_TOLERADO'),
            AL_DIA: estados('AL_DIA'),
            SIN_DEUDA: estados('SIN_DEUDA'),
          },
          saldo: String(suma((v) => v.saldo)),
          vencido: String(suma((v) => v.vencido)),
          saldoAFavor: String(suma((v) => v.saldoAFavor)),
          neto: String(suma((v) => v.neto)),
          tramos: {
            corriente: tramo('corriente'),
            '1-30': tramo('1-30'),
            '31-60': tramo('31-60'),
            '61-90': tramo('61-90'),
            '91+': tramo('91+'),
          },
        },
        A,
      ),
    );
  }
  if (!p.clientes.length)
    out.push({ texto: '' }, { texto: 'Ningun cliente de esta cartera tiene saldo.' });
  return out;
}

function totalesImpresos(
  rotulo: string,
  t: Omit<VendedorImpreso, 'vendedorId' | 'codigo' | 'nombre'>,
  A: number,
): LineaReporte[] {
  const estados = [
    `${t.porEstado.EN_MORA} en mora`,
    t.porEstado.VENCIDO_TOLERADO ? `${t.porEstado.VENCIDO_TOLERADO} vencidos con tolerancia` : '',
    `${t.porEstado.AL_DIA} al dia`,
    t.porEstado.SIN_DEUDA ? `${t.porEstado.SIN_DEUDA} sin deuda` : '',
  ]
    .filter(Boolean)
    .join(', ');
  return [
    { texto: '-'.repeat(A) },
    {
      texto:
        `${rotulo}: ${t.clientes} clientes listados de ${t.cartera} en cartera (${estados})`.slice(
          0,
          A,
        ),
      titulo: true,
    },
    fila(
      izq('Saldo $', 10),
      der(miles(n(t.saldo)), 14),
      izq('   Por vencer $', 16),
      der(miles(n(t.tramos.corriente)), 14),
      izq('   Vencido $', 13),
      der(miles(n(t.vencido)), 14),
      izq('   A favor $', 13),
      der(miles(n(t.saldoAFavor)), 12),
      izq('   Neto $', 10),
      der(miles(n(t.neto)), 14),
    ),
    fila(
      izq('1-30 $', 10),
      der(miles(n(t.tramos['1-30'])), 14),
      izq('   31-60 $', 16),
      der(miles(n(t.tramos['31-60'])), 14),
      izq('   61-90 $', 13),
      der(miles(n(t.tramos['61-90'])), 14),
      izq('   +90 $', 13),
      der(miles(n(t.tramos['91+'])), 12),
    ),
  ];
}

/** «10.305,00», como el pie de la cartola de Random. */
const dosDecimalesCl = (v: number) =>
  v.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export interface MedioImpreso {
  medio: string;
  numero?: string | null;
  banco?: string | null;
  fecha?: string | null;
  referencia?: string | null;
  monto: string;
}

export interface AplicacionImpresa {
  td: string;
  folio: string | null;
  emision?: string | null;
  vence?: string | null;
  valor?: string | null;
  abono: string;
  saldoHoy?: string | null;
  /** Con qué se pagó este documento: «Efectivo 1.000 + Transferencia op. 77 200» (trazabilidad). */
  pagadoCon?: string | null;
}

/** Código y nombre del medio como en Random (EFV EFECTIVO, CHV CHEQUE BANCARIO…). */
export const MEDIO_RANDOM: Record<string, string> = {
  EFECTIVO: 'EFV Efectivo',
  CHEQUE: 'CHV Cheque',
  CREDITO: 'TJV Tarjeta credito',
  DEBITO: 'TDV Tarjeta debito',
  TRANSFERENCIA: 'ATB Transferencia',
  VALE_VISTA: 'VVI Vale vista',
};

function seccionMedios(medios: MedioImpreso[]): LineaReporte[] {
  const out: LineaReporte[] = [
    { texto: 'REGISTRO DE PAGOS', titulo: true },
    { texto: GUIONES },
    fila(
      izq('TD / Medio', 21),
      izq('Numero', 14),
      izq('Banco', 18),
      izq('Fecha', 10),
      izq('Referencia', 14),
      der('Monto $', 14),
    ),
    { texto: GUIONES },
  ];
  for (const m of medios)
    out.push(
      fila(
        izq(MEDIO_RANDOM[m.medio] ?? m.medio, 21),
        izq(m.numero ?? '', 14),
        izq((m.banco ?? '').toUpperCase(), 18),
        izq(m.fecha ? ddmmaaaa(m.fecha) : '', 10),
        izq(m.referencia ?? '', 14),
        plata(m.monto, 14),
      ),
    );
  out.push({ texto: GUIONES });
  return out;
}

function seccionDocumentos(aps: AplicacionImpresa[], rotulo: string): LineaReporte[] {
  const conSaldo = aps.some((a) => a.saldoHoy != null);
  const out: LineaReporte[] = [
    { texto: '' },
    { texto: rotulo, titulo: true },
    { texto: GUIONES },
    fila(
      izq('TD', 4),
      izq('Numero', 12),
      izq('Fec.Emi.', 10),
      izq('Fec.Venc', 10),
      der('Valor Doc $', 14),
      der('Abono $', 14),
      conSaldo ? der('Saldo Act $', 14) : '',
    ),
    { texto: GUIONES },
  ];
  for (const a of aps)
    out.push(
      fila(
        izq(a.td, 4),
        izq(a.folio ?? '', 12),
        izq(a.emision ? ddmmaaaa(a.emision) : '', 10),
        izq(a.vence ? ddmmaaaa(a.vence) : '', 10),
        a.valor ? plata(a.valor, 14) : der('', 14),
        plata(a.abono, 14),
        conSaldo && a.saldoHoy != null ? plata(a.saldoHoy, 14) : '',
      ),
      ...(a.pagadoCon ? [{ texto: `     pagado con: ${a.pagadoCon}`.slice(0, ANCHO) }] : []),
    );
  if (!aps.length) out.push({ texto: 'Sin documentos (queda como anticipo / saldo a favor)' });
  out.push({ texto: GUIONES });
  return out;
}

function firmas(izquierda: string, derecha: string, centroTxt?: string): LineaReporte[] {
  const raya = '_'.repeat(26);
  if (centroTxt)
    return [
      { texto: '' },
      { texto: '' },
      { texto: '' },
      fila(centro(raya, 32).padEnd(32), centro(raya, 32).padEnd(32), centro(raya, 32)),
      fila(centro(izquierda, 32).padEnd(32), centro(centroTxt, 32).padEnd(32), centro(derecha, 32)),
    ];
  return [
    { texto: '' },
    { texto: '' },
    { texto: '' },
    fila(centro(raya, 48).padEnd(48), centro(raya, 48)),
    fila(centro(izquierda, 48).padEnd(48), centro(derecha, 48)),
  ];
}

/** Comprobante de ingreso de un cobro a cliente (recibo). */
export function textoComprobanteIngreso(p: {
  empresa: EmpresaImpresa;
  entidad: EntidadImpresa;
  numero: string;
  fecha: string;
  estado: string;
  medios: MedioImpreso[];
  aplicaciones: AplicacionImpresa[];
  total: string;
  aplicado: string;
  saldoAFavor: string;
  observacion?: string | null;
}): LineaReporte[] {
  return [
    ...cabecera(p.empresa, `COMPROBANTE DE INGRESO N° ${p.numero}`),
    ...datosEntidad(p.entidad, 'Entidad'),
    {
      texto: centro(
        `Fecha del pago: ${ddmmaaaa(diaDe(p.fecha))}${p.estado !== 'VIGENTE' ? `   ESTADO: ${p.estado}` : ''}`,
        ANCHO,
      ),
    },
    { texto: '' },
    ...seccionMedios(p.medios),
    ...seccionDocumentos(p.aplicaciones, 'DOCUMENTOS PAGADOS'),
    { texto: '' },
    fila(izq('TOTAL RECIBIDO $', 30), plata(p.total, 16)),
    fila(izq('APLICADO A DOCUMENTOS $', 30), plata(p.aplicado, 16)),
    fila(izq('SALDO A FAVOR DEL CLIENTE $', 30), plata(p.saldoAFavor, 16)),
    ...(p.observacion ? [{ texto: '' }, { texto: `Observacion: ${p.observacion}` }] : []),
    ...firmas('Recibi conforme (MADECI)', 'Firma / RUT del cliente'),
  ];
}

/** Comprobante de egreso de un pago a proveedor. */
export function textoComprobanteEgreso(p: {
  empresa: EmpresaImpresa;
  entidad: EntidadImpresa;
  numero: number;
  estado: string;
  fechaProgramada: string;
  pagadoEn: string | null;
  medio: MedioImpreso;
  comprobante: string | null;
  aplicaciones: AplicacionImpresa[];
  total: string;
  aplicado: string;
  anticipo: string;
  observacion?: string | null;
  /** Cuenta de la empresa desde la que se paga y cuenta del proveedor (2026-10-19). */
  cuentaOrigen?: string | null;
  cuentaDestino?: string | null;
  /** NC del proveedor descontadas en el pago: a qué factura rebajó cada una. */
  notasCredito?: Array<{ ncFolio: string | null; folio: string | null; monto: string }>;
}): LineaReporte[] {
  const ncs = p.notasCredito ?? [];
  const totalNc = ncs.reduce((s, x) => s + n(x.monto), 0);
  const fechas = [
    `Fecha programada: ${ddmmaaaa(p.fechaProgramada)}`,
    p.pagadoEn && `Pagado el: ${ddmmaaaa(diaDe(p.pagadoEn))}`,
    `Estado: ${p.estado.replace('_', ' ')}`,
  ]
    .filter(Boolean)
    .join('   ');
  return [
    ...cabecera(p.empresa, `COMPROBANTE DE EGRESO N° ${String(p.numero).padStart(6, '0')}`),
    ...datosEntidad(p.entidad, 'Proveedor'),
    { texto: centro(fechas, ANCHO) },
    ...(p.comprobante ? [{ texto: centro(`Comprobante: ${p.comprobante}`, ANCHO) }] : []),
    { texto: '' },
    ...seccionMedios([p.medio]),
    ...(p.cuentaOrigen ? [{ texto: `Desde la cuenta : ${p.cuentaOrigen}`.slice(0, ANCHO) }] : []),
    ...(p.cuentaDestino ? [{ texto: `A la cuenta     : ${p.cuentaDestino}`.slice(0, ANCHO) }] : []),
    ...seccionDocumentos(p.aplicaciones, 'DOCUMENTOS PAGADOS'),
    ...(ncs.length
      ? [
          { texto: '' },
          { texto: 'NOTAS DE CREDITO DESCONTADAS', titulo: true },
          ...ncs.map((x) =>
            fila(
              izq(`NCC ${x.ncFolio ?? ''}`, 16),
              izq(`rebaja FCC ${x.folio ?? ''}`, 24),
              plata(x.monto, 16),
            ),
          ),
        ]
      : []),
    { texto: '' },
    ...(totalNc ? [fila(izq('DESCONTADO CON NC $', 30), plata(totalNc, 16))] : []),
    fila(izq('TOTAL PAGADO $', 30), plata(p.total, 16)),
    fila(izq('APLICADO A DOCUMENTOS $', 30), plata(p.aplicado, 16)),
    fila(izq('ANTICIPO AL PROVEEDOR $', 30), plata(p.anticipo, 16)),
    ...(p.observacion ? [{ texto: '' }, { texto: `Observacion: ${p.observacion}` }] : []),
    ...firmas('Preparado por', 'Recibi conforme (proveedor)', 'Autorizado por'),
  ];
}
